<template>
  <section id="noticias" class="section-padding bg-navy-500" aria-labelledby="news-title">
    <div class="container-custom">
      <!-- Title -->
      <header class="text-center mb-12 animate-fade-in-up">
        <h2 id="news-title" class="section-title text-white">{{ $t('news.title') }}</h2>
        <p class="section-subtitle text-white/90 mt-4">
          {{ $t('news.subtitle') }}
        </p>
      </header>

      <!-- Instagram Grid -->
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-12" role="list" aria-label="Publicaciones recientes de Instagram">
        <article
          v-for="(post, index) in instagramPosts"
          :key="post.id"
          :class="[
            'group relative aspect-square overflow-hidden rounded-xl animate-fade-in-up',
            `delay-${(index % 5) * 100}`
          ]"
          role="listitem"
        >
          <a
            :href="post.url"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="`Ver publicación en Instagram: ${post.caption}`"
            class="block w-full h-full"
          >
            <!-- Image -->
            <img
              :src="post.image"
              :alt="`Publicación de Instagram de MotoBoy Chile: ${post.caption}`"
              class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              loading="lazy"
            />

            <!-- Overlay -->
            <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
              <div class="text-white text-sm line-clamp-3">
                {{ post.caption }}
              </div>
            </div>

            <!-- Instagram Icon -->
            <div class="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true">
              <div class="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                <font-awesome-icon :icon="['fab', 'instagram']" class="text-white text-sm" />
              </div>
            </div>

            <!-- Play Icon for Videos -->
            <div
              v-if="post.isVideo"
              class="absolute inset-0 flex items-center justify-center pointer-events-none"
              aria-label="Video"
            >
              <div class="w-16 h-16 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
                <svg class="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                </svg>
              </div>
            </div>
          </a>
        </article>
      </div>

      <!-- Follow Button -->
      <footer class="text-center animate-fade-in-up delay-300">
        <a
          href="https://www.instagram.com/motoboychile.oficial"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Seguir a MotoBoy Chile en Instagram"
          class="inline-flex items-center gap-3 bg-white hover:bg-primary text-navy-500 hover:text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:transform hover:scale-105 shadow-lg hover:shadow-2xl"
        >
          <font-awesome-icon :icon="['fab', 'instagram']" class="text-2xl" aria-hidden="true" />
          <span>Síguenos en Instagram</span>
          <font-awesome-icon icon="arrow-right" aria-hidden="true" />
        </a>
      </footer>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue';

interface InstagramPost {
  id: string;
  url: string;
  image: string;
  caption: string;
  isVideo: boolean;
}

// Posts de ejemplo - en producción se obtendrían de la API de Instagram
const instagramPosts = ref<InstagramPost[]>([
  {
    id: '1',
    url: 'https://www.instagram.com/p/DQrMMgLjj1h/',
    image: '/images/instagram/post-1.webp',
    caption: '👔 ¿Junior interno o Junior Corporativo? La diferencia está en el servicio',
    isVideo: false,
  },
  {
    id: '2',
    url: 'https://www.instagram.com/p/DQbuVygjvql/',
    image: '/images/instagram/post-2.webp',
    caption: '📦 ¿Cómo optimizar tu logística con última milla?',
    isVideo: false,
  },
  {
    id: '3',
    url: 'https://www.instagram.com/reel/DQZKm49gThS/',
    image: '/images/instagram/post-3.webp',
    caption: '👋 François Pouzet nos mandó este saludo en el día de MotoBoy',
    isVideo: true,
  },
  {
    id: '4',
    url: 'https://www.instagram.com/p/DQUEf-0DkqY/',
    image: '/images/instagram/post-4.webp',
    caption: '🛵 En Motoboy vivimos cosas que solo alguien en entregas entiende',
    isVideo: false,
  },
  {
    id: '5',
    url: 'https://www.instagram.com/p/DQJ_EqsEtRC/',
    image: '/images/instagram/post-5.webp',
    caption: '¿Sigues contratando juniors internos? Eso ya es cosa del pasado',
    isVideo: false,
  },
  {
    id: '6',
    url: 'https://www.instagram.com/reel/DQHU8OzDLOb/',
    image: '/images/instagram/post-6.webp',
    caption: '🚀 Llega Motoboy Turbo: el servicio de envíos del futuro',
    isVideo: true,
  },
  {
    id: '7',
    url: 'https://www.instagram.com/p/DQB7-3jjskb/',
    image: '/images/instagram/post-7.webp',
    caption: '⁠Ya son 25 años desde que nacimos: 📍 Así empezó todo',
    isVideo: false,
  },
  {
    id: '8',
    url: 'https://www.instagram.com/p/DP6NoYSjlXZ/',
    image: '/images/instagram/post-8.webp',
    caption: '🚀 ¿Cómo será Motoboy en el 2050? Imagina drones y vehículos autónomos',
    isVideo: false,
  },
  {
    id: '9',
    url: 'https://www.instagram.com/p/DPyfYd5jpUZ/',
    image: '/images/instagram/post-9.webp',
    caption: 'Como ⁠pioneros en mensajería express queremos compartir nuestra historia',
    isVideo: false,
  },
  {
    id: '10',
    url: 'https://www.instagram.com/p/DPv6woQDrIQ/',
    image: '/images/instagram/post-10.webp',
    caption: '¿Y si vamos con los primeros? 🤔 ¿Sabías que...?',
    isVideo: false,
  },
]);
</script>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
